import { Injectable, inject, signal, computed, effect } from '@angular/core';
import type { ProjectData, SceneData, ImageData } from '../../../shared/models';
import { cloneDeep } from '../../../shared/utils/clone';
import { EditorDataService } from './editor-data.service';
import { EditLockService } from './edit-lock.service';

interface DirtyEntry {
	path: string;
	data: unknown;
}

/**
 * Shared data layer for the editor.
 * Holds ALL loaded content (project + scenes + images) as signals.
 * All components read from here; edits update signals immediately.
 * Dirty tracking for auto-save.
 */
@Injectable()
export class EditorStateService {
	private dataService = inject(EditorDataService);
	private editLock = inject(EditLockService);

	// ── Core state ──────────────────────────────────────────────────────────
	readonly projectData = signal<ProjectData | undefined>(undefined);
	readonly projectSlug = signal('');
	readonly projectPath = computed(() => `/projects/${this.projectSlug()}/project.json`);

	/** Origin of the loaded project — mirrors `Project.sourceUrl` on the backend. */
	readonly projectSourceUrl = signal<string | undefined>(undefined);

	/**
	 * True when the loaded project was imported (`sourceUrl !== 'local'`).
	 * Mirrors the backend predicate in `utils/project-read-only.ts`. Kept
	 * separate from `isReadOnly` so the banner can tell imported apart from
	 * locked-by-another and show the right message.
	 */
	readonly isImportedReadOnly = computed(() => {
		const sourceUrl = this.projectSourceUrl();
		return sourceUrl != null && sourceUrl !== 'local';
	});

	/**
	 * The single read-only predicate every writable form/timeline/file-browser/
	 * Monaco surface reads to hide or disable itself. True when the project is
	 * imported OR another user holds the edit lock (spec/plans/edit-locks/).
	 */
	readonly isReadOnly = computed(() => this.isImportedReadOnly() || this.editLock.lockedByOther());

	/**
	 * DEV-531: whether pending JSON changes (scene/image/project edits) save
	 * automatically 600ms after the user stops typing. When off, edits still
	 * accumulate in `pendingChanges` — the toolbar's Save button and Ctrl+S
	 * remain the only way to persist them. Persisted across sessions like
	 * `ThemeService.isDark`; defaults to on.
	 */
	readonly autosaveEnabled = signal<boolean>(localStorage.getItem('wb-autosave-enabled') !== 'false');

	constructor() {
		effect(() => {
			localStorage.setItem('wb-autosave-enabled', String(this.autosaveEnabled()));
		});
	}

	/** Per-scene content, keyed by fetch path (e.g. /projects/slug/scenes/foo/scene.json) */
	private sceneMap = new Map<string, ReturnType<typeof signal<SceneData | undefined>>>();

	/** Per-image content, keyed by fetch path */
	private imageMap = new Map<string, ReturnType<typeof signal<ImageData | undefined>>>();

	/** Paths that have been modified since last save */
	private dirtyPaths = new Set<string>();

	/** Bumped on every scene/image/project update — timeline can watch this. */
	readonly version = signal(0);

	/**
	 * Latest URL rewrite pair emitted by `rewriteUrlInAll`. Editor listens to
	 * this to sync its local `formData` copy so a stale src can't clobber the
	 * backend's rewrite on the next save.
	 */
	readonly lastUrlRewrite = signal<{ oldUrl: string; newUrl: string; version: number } | null>(null);
	private urlRewriteCounter = 0;

	/**
	 * Latest "playbackType cleared" event emitted by `clearPlaybackTypeForMedia`.
	 * Editor listens to mirror the strip into the open form so the user sees the
	 * flip immediately and a stale `VIDEO_NP` doesn't ride a later save back.
	 */
	readonly lastPlaybackTypeClear = signal<{ mediaUrl: string; version: number } | null>(null);
	private playbackTypeClearCounter = 0;

	/**
	 * DEV-128: latest video-mask composite auto-fired by `EditorJobsService`
	 * (not from the open scene-form's own button click). `scene-form.ts`
	 * watches this to mirror the "Queued" job indicator under the button even
	 * when the composite was triggered centrally, not by its own click.
	 */
	readonly lastVideoMaskComposite = signal<{ videoBaseUrl: string; maskBaseUrl: string; jobId: string; mediaId: string; version: number } | null>(null);
	private videoMaskCompositeCounter = 0;

	notifyVideoMaskComposite(videoBaseUrl: string, maskBaseUrl: string, jobId: string, mediaId: string): void {
		this.videoMaskCompositeCounter += 1;
		this.lastVideoMaskComposite.set({ videoBaseUrl, maskBaseUrl, jobId, mediaId, version: this.videoMaskCompositeCounter });
	}

	/**
	 * Latest "expand this audio track" request, keyed by slug. Set when the
	 * user clicks an audio block in the timeline. `AudioTrackEditorComponent`
	 * watches this to force its section open and expand/scroll to the matching
	 * row, even though it lives nested inside chapter-form/project-form.
	 */
	readonly audioExpandRequest = signal<{ slug: string; version: number } | null>(null);
	private audioExpandCounter = 0;

	requestAudioExpand(slug: string): void {
		this.audioExpandCounter += 1;
		this.audioExpandRequest.set({ slug, version: this.audioExpandCounter });
	}

	// ── Project ─────────────────────────────────────────────────────────────

	loadProject(slug: string): void {
		this.projectSlug.set(slug);
		this.dataService.loadFile<ProjectData>(`/projects/${slug}/project.json`).subscribe({
			next: (data) => {
				this.projectData.set(data);
			},
		});
	}

	updateProject(patcher: (draft: ProjectData) => void): void {
		const current = this.projectData();
		if (!current) return;
		const clone = cloneDeep(current);
		patcher(clone);
		this.projectData.set(clone);
		this.dirtyPaths.add(this.projectPath());
	}

	// ── Scenes ──────────────────────────────────────────────────────────────

	/** Get or create a signal for a scene. Triggers lazy load if not cached. */
	getScene(path: string): ReturnType<typeof signal<SceneData | undefined>> {
		const existing = this.sceneMap.get(path);
		if (existing) return existing;
		const fresh = signal<SceneData | undefined>(undefined);
		this.sceneMap.set(path, fresh);
		this.dataService.loadFile<SceneData>(path).subscribe({
			next: (data) => fresh.set(data),
		});
		return fresh;
	}

	/** Get a scene signal without triggering a load. Returns undefined signal if not cached. */
	peekScene(path: string): SceneData | undefined {
		return this.sceneMap.get(path)?.();
	}

	updateScene(path: string, patcher: (draft: SceneData) => void): void {
		const sig = this.sceneMap.get(path);
		if (!sig) return;
		const current = sig();
		if (!current) return;
		const clone = cloneDeep(current);
		patcher(clone);
		sig.set(clone);
		this.dirtyPaths.add(path);
	}

	/** Set scene data directly (e.g. after a form/JSON edit). Bumps `version` so the timeline re-fetches. */
	setScene(path: string, data: SceneData): void {
		let sig = this.sceneMap.get(path);
		if (!sig) {
			sig = signal<SceneData | undefined>(data);
			this.sceneMap.set(path, sig);
		} else {
			sig.set(data);
		}
		this.version.update(v => v + 1);
	}

	/**
	 * Cache freshly-fetched scene data without bumping `version` — used when merely
	 * loading/viewing a scene (not editing it). Bumping `version` here would make the
	 * timeline re-fetch and lose its scroll position every time a scene is clicked.
	 */
	cacheScene(path: string, data: SceneData): void {
		const sig = this.sceneMap.get(path);
		if (sig) sig.set(data);
		else this.sceneMap.set(path, signal<SceneData | undefined>(data));
	}

	// ── Images ──────────────────────────────────────────────────────────────

	getImage(path: string): ReturnType<typeof signal<ImageData | undefined>> {
		const existing = this.imageMap.get(path);
		if (existing) return existing;
		const fresh = signal<ImageData | undefined>(undefined);
		this.imageMap.set(path, fresh);
		this.dataService.loadFile<ImageData>(path).subscribe({
			next: (data) => fresh.set(data),
		});
		return fresh;
	}

	peekImage(path: string): ImageData | undefined {
		return this.imageMap.get(path)?.();
	}

	updateImage(path: string, patcher: (draft: ImageData) => void): void {
		const sig = this.imageMap.get(path);
		if (!sig) return;
		const current = sig();
		if (!current) return;
		const clone = cloneDeep(current);
		patcher(clone);
		sig.set(clone);
		this.dirtyPaths.add(path);
	}

	setImage(path: string, data: ImageData): void {
		let sig = this.imageMap.get(path);
		if (!sig) {
			sig = signal<ImageData | undefined>(data);
			this.imageMap.set(path, sig);
		} else {
			sig.set(data);
		}
	}

	// ── Dirty tracking & save ───────────────────────────────────────────────

	readonly isDirty = computed(() => this.dirtyPaths.size > 0);

	getDirtyEntries(): DirtyEntry[] {
		const entries: DirtyEntry[] = [];
		for (const path of this.dirtyPaths) {
			if (path === this.projectPath()) {
				entries.push({ path, data: this.projectData() });
				continue;
			}
			const sceneSig = this.sceneMap.get(path);
			if (sceneSig) {
				entries.push({ path, data: sceneSig() });
				continue;
			}
			const imageSig = this.imageMap.get(path);
			if (imageSig) {
				entries.push({ path, data: imageSig() });
			}
		}
		return entries;
	}

	markClean(paths: string[]): void {
		for (const p of paths) this.dirtyPaths.delete(p);
	}

	markAllClean(): void {
		this.dirtyPaths.clear();
	}

	hasDirtyPath(path: string): boolean {
		return this.dirtyPaths.has(path);
	}

	// ── Helpers ──────────────────────────────────────────────────────────────

	/** Clear all cached data (e.g. when switching projects). */
	reset(): void {
		this.projectData.set(undefined);
		this.projectSlug.set('');
		this.projectSourceUrl.set(undefined);
		this.sceneMap.clear();
		this.imageMap.clear();
		this.dirtyPaths.clear();
	}

	/**
	 * Canonicalize every URL reference inside the given media's storage folder
	 * to `canonicalUrl`. Mirrors the backend's post-gen canonicalization: any
	 * string that starts with `<canonicalUrl minus extension>.` is rewritten to
	 * `canonicalUrl`. Keeps open forms' in-memory state aligned with the backend
	 * so a subsequent save can't push a stale `.png`/`.webp` back.
	 */
	canonicalizeMediaUrls(canonicalUrl: string): void {
		const prefix = canonicalUrl.replace(/\.[^./]+$/, '');
		if (!prefix || prefix === canonicalUrl) return;

		const project = this.projectData();
		if (project) {
			const cloned = cloneDeep(project);
			if (canonicalize(cloned, prefix, canonicalUrl)) this.projectData.set(cloned);
		}

		for (const sig of this.sceneMap.values()) {
			const current = sig();
			if (!current) continue;
			const cloned = cloneDeep(current);
			if (canonicalize(cloned, prefix, canonicalUrl)) sig.set(cloned);
		}

		for (const sig of this.imageMap.values()) {
			const current = sig();
			if (!current) continue;
			const cloned = cloneDeep(current);
			if (canonicalize(cloned, prefix, canonicalUrl)) sig.set(cloned);
		}

		this.version.update(v => v + 1);
		this.urlRewriteCounter += 1;
		this.lastUrlRewrite.set({ oldUrl: prefix, newUrl: canonicalUrl, version: this.urlRewriteCounter });
	}

	/**
	 * Strip `playbackType: 'VIDEO_NP'` from any cached scene whose
	 * `background.source.src` matches `mediaBaseUrl`. Used after video version
	 * generation completes — once playable versions exist, the placeholder
	 * `VIDEO_NP` flag is no longer needed and the player should treat the scene
	 * as a normal video. Marks affected paths dirty so auto-save persists.
	 */
	clearPlaybackTypeForMedia(mediaBaseUrl: string): void {
		if (!mediaBaseUrl) return;

		let affected = false;
		for (const [path, sig] of this.sceneMap.entries()) {
			const current = sig() as (SceneData & { playbackType?: string }) | undefined;
			if (!current) continue;
			if (current.playbackType !== 'VIDEO_NP') continue;
			if (current.background?.source?.src !== mediaBaseUrl) continue;
			const cloned = cloneDeep(current) as SceneData & { playbackType?: string };
			delete cloned.playbackType;
			sig.set(cloned);
			this.dirtyPaths.add(path);
			affected = true;
		}

		if (!affected) return;
		this.version.update(v => v + 1);
		this.playbackTypeClearCounter += 1;
		this.lastPlaybackTypeClear.set({ mediaUrl: mediaBaseUrl, version: this.playbackTypeClearCounter });
	}

	/**
	 * DEV-275: baseUrls of video backgrounds still flagged `VIDEO_NP` across cached
	 * scenes that belong to the mounted chapters (or project-level scenes, which have
	 * no `/chapters/<slug>/` path segment). The player skips fetch+decode for VIDEO_NP
	 * scenes — they render black — so while their versions are still generating the
	 * editor covers the preview with a "generating" overlay. Only scans `sceneMap`
	 * residents (the interactive session's loaded/dropped scenes); reads `version()`
	 * so the caller's computed re-runs when scenes are added, edited, or cleared.
	 */
	mountedVideoNpBackgrounds(mountedChapterSlugs: Set<string>): string[] {
		this.version();
		const urls = new Set<string>();
		for (const [path, sig] of this.sceneMap.entries()) {
			const scene = sig() as (SceneData & { playbackType?: string }) | undefined;
			if (scene?.playbackType !== 'VIDEO_NP') continue;
			const src = scene.background?.source?.src;
			if (!src) continue;
			const chapterMatch = path.match(/\/chapters\/([^/]+)\//);
			if (chapterMatch ? mountedChapterSlugs.has(chapterMatch[1]) : true) urls.add(src);
		}
		return [...urls];
	}

	/**
	 * For DEV-128 auto-composite: given a media baseUrl and which side it is
	 * (`video` = `background.source.src`, `mask` = `background.source.maskSrc`),
	 * return the baseUrl(s) of the paired side from any loaded scene that
	 * references both. Only scans scenes already resident in `sceneMap` — the
	 * auto-trigger is scoped to the interactive editor session, not a
	 * project-wide guarantee.
	 */
	findPairedMediaBaseUrl(mediaBaseUrl: string, side: 'video' | 'mask'): string[] {
		if (!mediaBaseUrl) return [];
		const paired = new Set<string>();
		for (const sig of this.sceneMap.values()) {
			const source = sig()?.background?.source;
			if (!source?.src || !source.maskSrc) continue;
			if (side === 'video' && source.src === mediaBaseUrl) paired.add(source.maskSrc);
			if (side === 'mask' && source.maskSrc === mediaBaseUrl) paired.add(source.src);
		}
		return [...paired];
	}

	/**
	 * Whether any currently-loaded scene references this media as its
	 * background source or mask source. Session-scoped like
	 * `findPairedMediaBaseUrl` — only scans scenes resident in `sceneMap`, not
	 * a project-wide guarantee.
	 */
	isMediaUsedInAnyScene(baseUrl: string): boolean {
		if (!baseUrl) return false;
		for (const sig of this.sceneMap.values()) {
			const source = sig()?.background?.source;
			if (source?.src === baseUrl || source?.maskSrc === baseUrl) return true;
		}
		return false;
	}
}

function canonicalize(node: unknown, prefix: string, canonicalUrl: string): boolean {
	if (node === null || typeof node !== 'object') return false;
	let changed = false;
	if (Array.isArray(node)) {
		for (let i = 0; i < node.length; i++) {
			const v = node[i];
			if (typeof v === 'string' && v !== canonicalUrl && v.startsWith(prefix + '.')) {
				node[i] = canonicalUrl; changed = true;
			} else if (v && typeof v === 'object') {
				if (canonicalize(v, prefix, canonicalUrl)) changed = true;
			}
		}
		return changed;
	}
	const obj = node as Record<string, unknown>;
	for (const k of Object.keys(obj)) {
		const v = obj[k];
		if (typeof v === 'string' && v !== canonicalUrl && v.startsWith(prefix + '.')) {
			obj[k] = canonicalUrl; changed = true;
		} else if (v && typeof v === 'object') {
			if (canonicalize(v, prefix, canonicalUrl)) changed = true;
		}
	}
	return changed;
}
